import { Fragment, memo, useEffect, useMemo, useRef, useState } from 'react'
import {
  DndContext,
  DragEndEvent,
  DragMoveEvent,
  DragStartEvent,
  closestCenter,
  pointerWithin,
  CollisionDetection
} from '@dnd-kit/core'

import {
  getSortedLineIndices,
  restrictToSpecificContainer,
  computeItemsAfterDrop,
  updateDropIndicators,
  hideDropIndicators,
  parseDropTarget,
  getDraggedGroup,
  clearGroupDragTransform,
  applyGroupDragTransform,
  findItemEl,
  getGroupBounds,
  resolveExternalGapState
} from './utils'

import * as Styled from './styled'
import DropLine from './DropLine'
import BetweenLinesGap from './BetweenLinesGap'
import { IProps, IDndItem, IGroupBoundsOffsets } from './types'
import { useCreateSnapModifier } from './hooks'
import { useDndSensors } from 'hooks/useDndSensors'

const multiLineCollisionDetection: CollisionDetection = args => {
  return pointerWithin(args).length > 0
    ? pointerWithin(args)
    : closestCenter(args)
}

const MultiLineDnd = ({
  data,
  boundingContainerId,
  dropLineSettings,
  resolveDropLineStyle,
  onDragEnd,
  onDragStart,
  isMultiLineAllowed = true,
  selectedIds,
  dropState
}: IProps) => {
  const [items, setItems] = useState<IDndItem[]>(data)
  // When a non-contiguous group is dragged onto a line and overlaps existing
  // items, `computeItemsAfterDrop` promotes it to a fresh line. Instead of
  // showing a misleading cascade indicator on the hovered line, we light up
  // the gap that represents the new line. `null` = no promotion in flight.
  const [promotedGapAfterLineIndex, setPromotedGapAfterLineIndex] = useState<
    number | null
  >(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const groupOffsetsRef = useRef<IGroupBoundsOffsets | null>(null)

  useEffect(() => setItems(data), [data])

  const { sensors } = useDndSensors()
  const {
    createSnapModifier,
    onDragStart: onSnapDragStart,
    hideMagnetizingLine
  } = useCreateSnapModifier(items, selectedIds)

  const handleDragStart = (event: DragStartEvent) => {
    const draggedItem = event.active.data.current as IDndItem | undefined
    if (draggedItem) {
      const group = getDraggedGroup(items, draggedItem.id, selectedIds)
      if (group.length > 1) {
        const { groupLeftEdge, groupSpanLength } = getGroupBounds(group)
        groupOffsetsRef.current = {
          leftOffsetFromActive: groupLeftEdge - draggedItem.left,
          rightOffsetFromActive:
            groupLeftEdge +
            groupSpanLength -
            (draggedItem.left + draggedItem.width)
        }
      } else {
        groupOffsetsRef.current = null
      }
    }
    onSnapDragStart()
    onDragStart?.()
  }

  const handleDragMove = (event: DragMoveEvent) => {
    const { delta, active, over } = event
    const target = parseDropTarget(over)
    const draggedItem = active.data.current as IDndItem

    if (!target || !containerRef.current || !draggedItem) return

    const { items: updatedItems, didPromoteToNewLine } = computeItemsAfterDrop({
      draggedItem,
      items,
      target,
      containerWidth: containerRef.current.clientWidth,
      deltaX: delta.x,
      selectedIds
    })

    const group = getDraggedGroup(items, draggedItem.id, selectedIds)
    const groupIds = new Set(group.map(g => g.id))

    // 1. Visually move group members
    if (group.length > 1) {
      applyGroupDragTransform({
        containerRef,
        group,
        activeId: active.id,
        deltaX: delta.x,
        deltaY: delta.y
      })
    }

    // 2. Cascade Preview (Move items pushed out of the way). Skip when the
    // group was promoted to a new line — `shiftItemsForGapInsert` bumps the
    // unrelated rows down by 1, but the user shouldn't see those rows
    // visually shift during drag (they only really move on drop).
    if (!didPromoteToNewLine) {
      updatedItems.forEach(item => {
        if (item.id === active.id || groupIds.has(item.id)) return
        const el = findItemEl(item.id, containerRef)
        if (el) el.style.left = `${item.left}px`
      })
    }

    if (target.kind === 'gap') {
      hideDropIndicators(containerRef)
      if (promotedGapAfterLineIndex !== null) {
        setPromotedGapAfterLineIndex(null)
      }
      return
    }

    // 3. Drop indicator vs promoted-gap highlight
    // A non-contiguous group dropped on a line that can't hold it gets
    // promoted to a fresh line via `shiftItemsForGapInsert`. Skip the
    // cascade indicator on the hovered line and light up the gap above
    // it instead (that's where `shiftItemsForGapInsert` opens up the slot).
    const nextPromotedGap = didPromoteToNewLine ? target.lineIndex - 1 : null
    if (nextPromotedGap !== promotedGapAfterLineIndex) {
      setPromotedGapAfterLineIndex(nextPromotedGap)
    }

    if (didPromoteToNewLine) {
      hideDropIndicators(containerRef)
      return
    }

    const draggedAtNew = updatedItems.find(i => i.id === active.id) as IDndItem
    const draggingItems = updatedItems.filter(i => groupIds.has(i.id))
    const { groupLeftEdge, groupSpanLength } = getGroupBounds(draggingItems)

    updateDropIndicators({
      containerRef,
      lineIndex: draggedAtNew.lineIndex,
      left: groupLeftEdge,
      width: groupSpanLength
    })
  }

  const handleDragEnd = (event: DragEndEvent) => {
    if (!containerRef.current) return
    hideDropIndicators(containerRef)

    const { delta, active, over } = event
    const draggedItem = active.data.current as IDndItem
    const group = draggedItem
      ? getDraggedGroup(items, draggedItem.id, selectedIds)
      : []

    // Clear styles immediately
    clearGroupDragTransform(containerRef, group, active.id)
    hideMagnetizingLine()
    groupOffsetsRef.current = null
    setPromotedGapAfterLineIndex(null)

    const target = parseDropTarget(over)
    if (!draggedItem || !target) return

    const { items: updatedItems } = computeItemsAfterDrop({
      draggedItem,
      items,
      target,
      containerWidth: containerRef.current.clientWidth,
      deltaX: delta.x,
      selectedIds
    })

    // Flicker Prevention: Sync DOM before React render
    const movingItems = group.length > 0 ? group : [{ id: active.id as string }]
    movingItems.forEach(member => {
      const newPos = updatedItems.find(i => i.id === member.id)
      const el = findItemEl(member.id, containerRef)
      if (newPos && el) el.style.left = `${newPos.left}px`
    })

    setItems(updatedItems)
    onDragEnd?.(updatedItems)
  }

  const modifiers = useMemo(() => {
    if (!boundingContainerId) return []
    return [
      restrictToSpecificContainer(boundingContainerId, groupOffsetsRef.current),
      createSnapModifier
    ]
  }, [boundingContainerId, createSnapModifier])

  const lineIndices = useMemo(() => {
    const sorted = getSortedLineIndices(items)
    return sorted.length === 0 && isMultiLineAllowed ? [0] : sorted
  }, [items, isMultiLineAllowed])

  const lineItemsMap = useMemo(() => {
    const map = new Map<number, IDndItem[]>()
    items.forEach(item => {
      const list = map.get(item.lineIndex) ?? []
      list.push(item)
      map.set(item.lineIndex, list)
    })
    return map
  }, [items])

  const { isDragActive, isOver: isTopGapHovered } = resolveExternalGapState(
    -1,
    dropState
  )

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={multiLineCollisionDetection}
      onDragEnd={handleDragEnd}
      onDragStart={handleDragStart}
      onDragMove={handleDragMove}
      modifiers={modifiers}
    >
      <Styled.MultiLineDndContainer ref={containerRef}>
        {isMultiLineAllowed && (
          <BetweenLinesGap
            afterLineIndex={-1}
            className={dropLineSettings?.className}
            externalIsDragActive={isDragActive}
            externalIsOver={isTopGapHovered || promotedGapAfterLineIndex === -1}
          />
        )}

        {lineIndices.map(lineIdx => {
          const lineItems = lineItemsMap.get(lineIdx) ?? []
          const mergedStyle = {
            ...dropLineSettings?.style,
            ...resolveDropLineStyle?.(lineIdx, lineItems)
          }
          const externalGap = resolveExternalGapState?.(lineIdx, dropState)
          const isPromotedGap = promotedGapAfterLineIndex === lineIdx

          return (
            <Fragment key={lineIdx}>
              <DropLine
                lineIndex={lineIdx}
                items={lineItems}
                className={dropLineSettings?.className}
                style={mergedStyle}
              />
              {isMultiLineAllowed && (
                <BetweenLinesGap
                  afterLineIndex={lineIdx}
                  className={dropLineSettings?.className}
                  externalIsDragActive={externalGap?.isDragActive}
                  externalIsOver={externalGap?.isOver || isPromotedGap}
                />
              )}
            </Fragment>
          )
        })}
      </Styled.MultiLineDndContainer>
    </DndContext>
  )
}

export default memo(MultiLineDnd)
